// use client: attaches a document-level click listener to every CMS-parsed
// `.description`/`.tnc-link` node to intercept external links into a
// confirmation Modal, and reads `formRef` from the store for scroll-to-form.
'use client'

import type { PromotionCardView, PromotionDetailView } from '@/types/cms'

import '../personal.css'

import { useEffect, useRef, useState } from 'react'
import { Button, Card, Col, Modal, Row } from 'react-bootstrap'
import { has } from 'lodash'
import moment from 'moment'
import { useRouter } from 'next/navigation'

import calendar_icon from '@/assets/images/calendar_icon.svg'
import circle_left from '@/assets/images/circle_left.svg'
import circle_right from '@/assets/images/circle_right.svg'
import gift_icon from '@/assets/images/gift_icon.svg'
import { NormalButton, PrimaryButton } from '@/components/buttons'
import { CardCarousel } from '@/components/carousel/CardCarousel'
import LetsConnect from '@/sections/ComingSoon/LetsConnect'
import useStore from '@/stores/Store'
import { handleOpenPdf, parseCmsHtml } from '@/utils'
import { cmsImageSrc } from '@/utils/cmsImageSrc'

/*
 * Swiper breakpoints are min-width floors, so the component props are the
 * <=480px case and each entry overrides upward. react-slick expressed the same
 * ladder as max-width ceilings (480 / 767 / 991 / 1399), inverted.
 *
 * The 481-767 band used slick's `centerMode` + `centerPadding: '60px'`: one card
 * centred with a 60px peek of its neighbours either side. Swiper's equivalent is
 * `centeredSlides` with `slidesPerView: 'auto'` and the slide width set in CSS
 * (`.personal-promotions .swiper-slide`, personal.css) — same mechanism slick
 * used internally, expressed declaratively.
 */
const BREAKPOINTS = {
  481: { slidesPerView: 'auto' as const, slidesPerGroup: 1, spaceBetween: 0, centeredSlides: true },
  768: { slidesPerView: 2, slidesPerGroup: 2, spaceBetween: 0, centeredSlides: false },
  992: { slidesPerView: 3, slidesPerGroup: 3, spaceBetween: 0, centeredSlides: false },
  1400: { slidesPerView: 4, slidesPerGroup: 4, spaceBetween: 0, centeredSlides: false }
}

function PromotionsMain({
  initialData,
  allPromotions = []
}: {
  initialData?: PromotionDetailView | null
  /**
   * The whole /promotion-and-campaigns collection, powering the "Ongoing
   * Promotions" slider. It used to be fetched on mount because the route seeded
   * only the current promotion; the route already had the full collection in
   * hand to resolve that slug, so seeding it here costs nothing at build time.
   * It is the card projection, not the full items — every detail page embeds
   * this list, so the rich-text bodies must not ride along.
   * See tests/no-client-cms-fetch.spec.ts.
   */
  allPromotions?: PromotionCardView[]
}) {
  const promotionHighlightRef = useRef<HTMLDivElement>(null)
  const promotionsContent: PromotionCardView[] = allPromotions
  // The route resolves the slug at build time, so the detail is a prop, not
  // state derived from the (slim) slider list.
  const selectedPromo = initialData
  const router = useRouter()
  const [isAlert, setIsAlert] = useState(false)
  const [externalLink, setExternalLink] = useState<string | URL>()
  const { formRef } = useStore()

  // const sliderRef = useRef(null);

  // useEffect(() => {
  //   if (sliderRef.current) {
  //     setTimeout(() => {
  //       sliderRef.current.slickGoTo(0);
  //     }, 100);
  //   }
  // }, []);

  const handleLinkClick = event => {
    const link = event?.target?.closest('a')

    if (link) {
      const url = new URL(link.href)

      if (url.origin !== window.location.origin) {
        // if (url.origin != "https://www.alrajhibank.com.my") {
        event.preventDefault()
        setIsAlert(true)
        setExternalLink(url)
      }
    } else {
      if (typeof event === 'string') {
        if (!event?.includes('alrajhibank.com.my')) {
          setIsAlert(true)
          setExternalLink(event)
        } else {
          handleOpenPdf(event)
        }
      }
    }
  }

  useEffect(() => {
    const elements = document.querySelectorAll('.description, .tnc-link')
    elements.forEach(element => {
      element.addEventListener('click', handleLinkClick)
    })

    return () => {
      elements.forEach(element => {
        element.removeEventListener('click', handleLinkClick)
      })
    }
  }, [selectedPromo])

  const handlePromotionClick = (item: PromotionCardView) => {
    if (item.href) {
      // .assign() rather than `location.href =`: an identical navigation, but a
      // method call instead of a mutation of a global, which React Compiler can
      // compile around.
      window.location.assign(item.href)
      return
    }

    if (item.isExternalLink) {
      setExternalLink(item.externalLink)
      setIsAlert(true)
      return
    }

    if (item.isRizeCampaign) {
      router.push(`/rize/promotions#${item.slug}`)
      return
    }

    router.push(`/personal/promotions/${item.slug}`)
    promotionHighlightRef.current?.scrollIntoView()
  }

  return (
    <>
      <style>
        {`
          .jumbotron .background-img {
            background: none;
          }
        `}
      </style>
      <Modal
        show={isAlert}
        onHide={() => setIsAlert(false)}
        centered
        size="lg"
        className="alert-modal"
        aria-label="Leaving alrajhi bank Malaysia website"
      >
        <Modal.Body>
          <h3>
            <b>You are now leaving alrajhi bank Malaysia website</b>
          </h3>
          <p>
            You are now leaving alrajhi bank Malaysia's (“the Bank”) website. If you decide to
            access any third party websites linked to the Bank’s website, you do this entirely at
            your own risk. The Bank is not responsible for nor endorses or shall have any liability
            arising from such sites, products or services offered therein.
          </p>
        </Modal.Body>
        <Modal.Footer
          style={{ border: 'none' }}
          className="d-flex justify-content-center flex-nowrap"
        >
          <NormalButton
            text="Close"
            onClick={() => {
              setIsAlert(false)
            }}
            style={{ width: '50%' }}
          />
          <PrimaryButton
            text="Continue"
            onClick={() => {
              window.open(externalLink, '_blank')
              setIsAlert(false)
            }}
            style={{ width: '50%' }}
            id="visit-now"
          />
        </Modal.Footer>
      </Modal>
      <div>
        <div className="container promotion-highlight my-5" ref={promotionHighlightRef}>
          <Row className="g-5 align-items-start">
            <Col lg={6}>
              <div className="promo-image-wrapper">
                <img
                  src={cmsImageSrc(
                    selectedPromo?.imageLink ? selectedPromo.imageLink : selectedPromo?.image
                  )}
                  alt="promo"
                  className="promo-image"
                />
              </div>
            </Col>

            <Col lg={6}>
              <div className="promo-details">
                <h1 className="promo-title pe-md-5">
                  {selectedPromo?.pageTitle
                    ? parseCmsHtml(selectedPromo.pageTitle)
                    : parseCmsHtml(selectedPromo?.title || '')}
                </h1>

                {selectedPromo?.startDate && (
                  <div className="campaign-period mb-0 d-block d-lg-none">
                    Campaign Period:
                    <br></br>
                    <p className="mt-2">
                      <span>
                        {`${moment(selectedPromo.startDate).format('DD MMM YYYY')} - ${moment(
                          selectedPromo.endDate
                        ).format('DD MMM YYYY')}`}
                      </span>
                    </p>
                  </div>
                )}

                <div className="promo-content-box promo-content pt-md-2 mt-md-0 pe-md-5">
                  {selectedPromo?.startDate && (
                    <div className="campaign-period mb-5 d-none d-lg-block">
                      Campaign Period: {''}
                      <span>
                        {`${moment(selectedPromo.startDate).format('DD MMM YYYY')} - ${moment(
                          selectedPromo.endDate
                        ).format('DD MMM YYYY')}`}
                      </span>
                    </div>
                  )}

                  {selectedPromo?.subtitle && (
                    <div className="subtitle">{selectedPromo.subtitle}</div>
                  )}

                  {typeof selectedPromo?.contentTemplate === 'string' && (
                    <div className="description">{parseCmsHtml(selectedPromo.contentTemplate)}</div>
                  )}

                  {has(selectedPromo, 'footer') && (
                    <div className="mt-4">{parseCmsHtml(selectedPromo.footer)}</div>
                  )}

                  <div className="d-flex gap-3 flex-wrap promotion-links mt-md-5 mt-4">
                    {selectedPromo?.tncLink && (
                      <Button
                        className=" promo-btn tnc-link flex-fill mb-3 bh-white"
                        onClick={() => handleLinkClick(selectedPromo.tncLink)}
                      >
                        Terms and Conditions
                      </Button>
                    )}

                    {selectedPromo?.inquireNow && (
                      <Button
                        className="applybtn flex-fill promo-btn mb-3 "
                        onClick={() => {
                          if (formRef.current) {
                            const offset = 410
                            const topPosition =
                              formRef.current.getBoundingClientRect().top +
                              window.pageYOffset -
                              offset

                            window.scrollTo({
                              top: topPosition,
                              behavior: 'smooth'
                            })
                          }
                        }}
                      >
                        {selectedPromo.inquireNow.label}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <div className="container personal-promotions">
          <h4 className="subheading text-center text-md-start">Ongoing Promotions</h4>
          <CardCarousel
            slidesPerView="auto"
            slidesPerGroup={1}
            spaceBetween={20}
            breakpoints={BREAKPOINTS}
            arrows={{ prevSrc: circle_left.src, nextSrc: circle_right.src }}
            arrowsClassName="carousel-arrow--hide-mobile"
            showDots
            dotsClassName="promotions-carousel-dots"
          >
            {promotionsContent.map(item => (
              <div
                key={item.slug}
                className="personal-promotion-item"
                onClick={() => handlePromotionClick(item)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handlePromotionClick(item)
                  }
                }}
              >
                <Card
                  className="card-style"
                  data-aos="fade-left"
                  data-aos-delay={`${Number(item.id) + 1}00`}
                >
                  <Card.Img
                    style={{
                      borderTopRightRadius: '20px',
                      borderTopLeftRadius: '20px',
                      width: '100%',
                      objectFit: 'cover'
                    }}
                    variant="top"
                    src={cmsImageSrc(item.imageLink ? item.imageLink : item.image)}
                  />
                  <Card.Body className="ps-0">
                    <div className="d-flex flex-row justify-content-between align-items-center "></div>
                    <div className="title mb-md-3 mb-3" style={{ color: '#000045' }}>
                      {item.pageTitle
                        ? parseCmsHtml(item.pageTitle || '')
                        : parseCmsHtml(item.title || '')}
                    </div>
                    <div className="d-flex flex-column">
                      <div className="d-flex flex-row align-items-center gap-2 mb-2">
                        <img src={calendar_icon.src} height="18px" alt="calendar_icon" />
                        <span
                          className="text-black-50 promo-meta-text"
                          style={{ paddingLeft: '5px', paddingTop: '2px' }}
                        >
                          {item.period && item.period}
                          {item?.startDate &&
                            `${moment(item?.startDate).format('DD MMM YYYY')} to ${moment(
                              item?.endDate
                            ).format('DD MMM YYYY')}`}
                        </span>
                      </div>
                      <div className="d-flex flex-row align-items-center gap-2">
                        <img src={gift_icon.src} height="18px" alt="gift_icon" />
                        <span
                          className="text-black-50 promo-meta-text"
                          style={{ paddingLeft: '5px', paddingTop: '2px' }}
                        >
                          {item.type}
                        </span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </CardCarousel>
        </div>

        <LetsConnect />
      </div>
    </>
  )
}

export default PromotionsMain
